/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Recap
 * OpenAPI spec version: 1
 */
import * as zod from 'zod';

import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useApiFetch } from '../client.ts';
import type { ErrorType } from '../client.ts';
import { useMutationBehavior } from '../mutations.ts';
import { useQueryBehavior } from '../queries.ts';
import { SignInCodeSent, SignInResult, SsoProvider, SsoStartResult, User } from './schemas.ts';
import type {
    AccountDeletion,
    EmailChangeInput,
    EmailVerificationInput,
    ErrorResponse,
    SignInCodeInput,
    SignInCodeVerification,
    SsoStart,
    UploadMyAvatarBody,
    UserUpdate,
} from './schemas.ts';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

export const getGetMeUrl = () => {
    return `/me`;
};

/**
 * @summary Get Me
 */
export const useGetMeHook = (): ((options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => Promise<User>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetMeUrl(), {
            ...options,
            method: 'GET',

            schema: User,
        });
    };
};

export const getGetMeQueryKey = () => {
    return ['me'] as const;
};

export const useGetMeQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
    TError = ErrorType<ErrorResponse>,
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>, TError, TData>>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetMeQueryKey();

    const getMe = useGetMeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>> = ({ signal }) =>
        getMe({ signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        {},
        { url: `/me`, operationId: 'get_me', operationName: 'getMe' }
    );

    return customOptions as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type GetMeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>>;
export type GetMeQueryError = ErrorType<ErrorResponse>;

export function useGetMe<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMe<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMe<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Me
 */

export function useGetMe<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMeHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetMeQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateMeUrl = () => {
    return `/me`;
};

/**
 * @summary Update Me
 */
export const useUpdateMeHook = (): ((
    userUpdate: UserUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<User>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (userUpdate: UserUpdate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getUpdateMeUrl(), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(userUpdate),
            schema: User,
        });
    };
};

export const getUpdateMeMutationKey = () => ['updateMe'] as const;

export const useUpdateMeMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
        TError,
        UpdateMeMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
    TError,
    UpdateMeMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateMeMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateMe = useUpdateMeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
        UpdateMeMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return updateMe(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me` },
        { operationId: 'update_me', operationName: 'updateMe' }
    );

    return customOptions;
};

export type UpdateMeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>>;
export type UpdateMeMutationBody = UserUpdate;
export type UpdateMeMutationError = ErrorType<ErrorResponse>;
export type UpdateMeMutationVariables = { data: UserUpdate };

/**
 * @summary Update Me
 */
export const useUpdateMe = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
            TError,
            UpdateMeMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateMeHook>>>,
    TError,
    UpdateMeMutationVariables,
    TContext
> => {
    return useMutation(useUpdateMeMutationOptions(options), queryClient);
};
export const getDeleteMeUrl = () => {
    return `/me`;
};

/**
 * @summary Delete Me
 */
export const useDeleteMeHook = (): ((
    accountDeletion: AccountDeletion,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (accountDeletion: AccountDeletion, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteMeUrl(), {
            ...options,
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(accountDeletion),
        });
    };
};

export const getDeleteMeMutationKey = () => ['deleteMe'] as const;

export const useDeleteMeMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteMeHook>>>,
        TError,
        DeleteMeMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteMeHook>>>,
    TError,
    DeleteMeMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteMeMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteMe = useDeleteMeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteMeHook>>>,
        DeleteMeMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return deleteMe(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me` },
        { operationId: 'delete_me', operationName: 'deleteMe' }
    );

    return customOptions;
};

export type DeleteMeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteMeHook>>>>;
export type DeleteMeMutationBody = AccountDeletion;
export type DeleteMeMutationError = ErrorType<ErrorResponse>;
export type DeleteMeMutationVariables = { data: AccountDeletion };

/**
 * @summary Delete Me
 */
export const useDeleteMe = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteMeHook>>>,
            TError,
            DeleteMeMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteMeHook>>>,
    TError,
    DeleteMeMutationVariables,
    TContext
> => {
    return useMutation(useDeleteMeMutationOptions(options), queryClient);
};
export const getUploadMyAvatarUrl = () => {
    return `/me/avatar`;
};

/**
 * @summary Upload My Avatar
 */
export const useUploadMyAvatarHook = (): ((
    uploadMyAvatarBody: UploadMyAvatarBody,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<User>) => {
    const customFetcher = useApiFetch();
    return (uploadMyAvatarBody: UploadMyAvatarBody, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        const formData = new FormData();
        formData.append(`file`, uploadMyAvatarBody.file);

        return customFetcher(getUploadMyAvatarUrl(), {
            ...options,
            method: 'POST',
            body: formData,
            schema: User,
        });
    };
};

export const getUploadMyAvatarMutationKey = () => ['uploadMyAvatar'] as const;

export const useUploadMyAvatarMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadMyAvatarHook>>>,
        TError,
        UploadMyAvatarMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadMyAvatarHook>>>,
    TError,
    UploadMyAvatarMutationVariables,
    TContext
> => {
    const mutationKey = getUploadMyAvatarMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const uploadMyAvatar = useUploadMyAvatarHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadMyAvatarHook>>>,
        UploadMyAvatarMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return uploadMyAvatar(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me/avatar` },
        { operationId: 'upload_my_avatar', operationName: 'uploadMyAvatar' }
    );

    return customOptions;
};

export type UploadMyAvatarMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUploadMyAvatarHook>>>>;
export type UploadMyAvatarMutationBody = UploadMyAvatarBody;
export type UploadMyAvatarMutationError = ErrorType<ErrorResponse>;
export type UploadMyAvatarMutationVariables = { data: UploadMyAvatarBody };

/**
 * @summary Upload My Avatar
 */
export const useUploadMyAvatar = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadMyAvatarHook>>>,
            TError,
            UploadMyAvatarMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadMyAvatarHook>>>,
    TError,
    UploadMyAvatarMutationVariables,
    TContext
> => {
    return useMutation(useUploadMyAvatarMutationOptions(options), queryClient);
};
export const getDeleteMyAvatarUrl = () => {
    return `/me/avatar`;
};

/**
 * @summary Delete My Avatar
 */
export const useDeleteMyAvatarHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<User>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteMyAvatarUrl(), {
            ...options,
            method: 'DELETE',

            schema: User,
        });
    };
};

export const getDeleteMyAvatarMutationKey = () => ['deleteMyAvatar'] as const;

export const useDeleteMyAvatarMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteMyAvatarHook>>>,
        TError,
        void,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteMyAvatarHook>>>, TError, void, TContext> => {
    const mutationKey = getDeleteMyAvatarMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteMyAvatar = useDeleteMyAvatarHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteMyAvatarHook>>>, void> = () => {
        return deleteMyAvatar(requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me/avatar` },
        { operationId: 'delete_my_avatar', operationName: 'deleteMyAvatar' }
    );

    return customOptions;
};

export type DeleteMyAvatarMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteMyAvatarHook>>>>;

export type DeleteMyAvatarMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Delete My Avatar
 */
export const useDeleteMyAvatar = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteMyAvatarHook>>>,
            TError,
            void,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useDeleteMyAvatarHook>>>, TError, void, TContext> => {
    return useMutation(useDeleteMyAvatarMutationOptions(options), queryClient);
};
export const getGetUserAvatarUrl = (userId: string | null) => {
    return `/users/${userId}/avatar`;
};

/**
 * @summary Get User Avatar
 */
export const useGetUserAvatarHook = (): ((
    userId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (userId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetUserAvatarUrl(userId), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetUserAvatarQueryKey = (userId: string | null) => {
    return ['users', userId, 'avatar'] as const;
};

export const useGetUserAvatarQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    userId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetUserAvatarQueryKey(userId);

    const getUserAvatar = useGetUserAvatarHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>> = ({ signal }) =>
        getUserAvatar(userId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: userId !== null && userId !== undefined, ...queryOptions },
        { userId },
        { url: `/users/${userId}/avatar`, operationId: 'get_user_avatar', operationName: 'getUserAvatar' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserAvatarQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>>;
export type GetUserAvatarQueryError = ErrorType<ErrorResponse>;

export function useGetUserAvatar<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    userId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserAvatar<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    userId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserAvatar<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    userId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Avatar
 */

export function useGetUserAvatar<
    TData = Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    userId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUserAvatarHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetUserAvatarQueryOptions(userId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getDeleteMySsoIdentityUrl = (identityId: string) => {
    return `/me/sso-identities/${identityId}`;
};

/**
 * @summary Delete My Sso Identity
 */
export const useDeleteMySsoIdentityHook = (): ((
    identityId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (identityId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteMySsoIdentityUrl(identityId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getDeleteMySsoIdentityMutationKey = () => ['deleteMySsoIdentity'] as const;

export const useDeleteMySsoIdentityMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteMySsoIdentityHook>>>,
        TError,
        DeleteMySsoIdentityMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteMySsoIdentityHook>>>,
    TError,
    DeleteMySsoIdentityMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteMySsoIdentityMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteMySsoIdentity = useDeleteMySsoIdentityHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteMySsoIdentityHook>>>,
        DeleteMySsoIdentityMutationVariables
    > = (props) => {
        const { identityId } = props ?? {};

        return deleteMySsoIdentity(identityId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me/sso-identities/{identityId}` },
        { operationId: 'delete_my_sso_identity', operationName: 'deleteMySsoIdentity' }
    );

    return customOptions;
};

export type DeleteMySsoIdentityMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteMySsoIdentityHook>>>
>;

export type DeleteMySsoIdentityMutationError = ErrorType<ErrorResponse>;
export type DeleteMySsoIdentityMutationVariables = { identityId: string };

/**
 * @summary Delete My Sso Identity
 */
export const useDeleteMySsoIdentity = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteMySsoIdentityHook>>>,
            TError,
            DeleteMySsoIdentityMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteMySsoIdentityHook>>>,
    TError,
    DeleteMySsoIdentityMutationVariables,
    TContext
> => {
    return useMutation(useDeleteMySsoIdentityMutationOptions(options), queryClient);
};
export const getRequestMyAccessUrl = () => {
    return `/me/access-request`;
};

/**
 * @summary Request My Access
 */
export const useRequestMyAccessHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<User>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getRequestMyAccessUrl(), {
            ...options,
            method: 'POST',

            schema: User,
        });
    };
};

export const getRequestMyAccessMutationKey = () => ['requestMyAccess'] as const;

export const useRequestMyAccessMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRequestMyAccessHook>>>,
        TError,
        void,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useRequestMyAccessHook>>>, TError, void, TContext> => {
    const mutationKey = getRequestMyAccessMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const requestMyAccess = useRequestMyAccessHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useRequestMyAccessHook>>>, void> = () => {
        return requestMyAccess(requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me/access-request` },
        { operationId: 'request_my_access', operationName: 'requestMyAccess' }
    );

    return customOptions;
};

export type RequestMyAccessMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useRequestMyAccessHook>>>>;

export type RequestMyAccessMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Request My Access
 */
export const useRequestMyAccess = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRequestMyAccessHook>>>,
            TError,
            void,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useRequestMyAccessHook>>>, TError, void, TContext> => {
    return useMutation(useRequestMyAccessMutationOptions(options), queryClient);
};
export const getChangeEmailUrl = () => {
    return `/me/email`;
};

/**
 * @summary Change Email
 */
export const useChangeEmailHook = (): ((
    emailChangeInput: EmailChangeInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<User>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (emailChangeInput: EmailChangeInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getChangeEmailUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(emailChangeInput),
            schema: User,
        });
    };
};

export const getChangeEmailMutationKey = () => ['changeEmail'] as const;

export const useChangeEmailMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useChangeEmailHook>>>,
        TError,
        ChangeEmailMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useChangeEmailHook>>>,
    TError,
    ChangeEmailMutationVariables,
    TContext
> => {
    const mutationKey = getChangeEmailMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const changeEmail = useChangeEmailHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useChangeEmailHook>>>,
        ChangeEmailMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return changeEmail(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me/email` },
        { operationId: 'change_email', operationName: 'changeEmail' }
    );

    return customOptions;
};

export type ChangeEmailMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useChangeEmailHook>>>>;
export type ChangeEmailMutationBody = EmailChangeInput;
export type ChangeEmailMutationError = ErrorType<ErrorResponse>;
export type ChangeEmailMutationVariables = { data: EmailChangeInput };

/**
 * @summary Change Email
 */
export const useChangeEmail = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useChangeEmailHook>>>,
            TError,
            ChangeEmailMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useChangeEmailHook>>>,
    TError,
    ChangeEmailMutationVariables,
    TContext
> => {
    return useMutation(useChangeEmailMutationOptions(options), queryClient);
};
export const getVerifyEmailUrl = () => {
    return `/me/email/verify`;
};

/**
 * @summary Verify Email
 */
export const useVerifyEmailHook = (): ((
    emailVerificationInput: EmailVerificationInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<User>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        emailVerificationInput: EmailVerificationInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getVerifyEmailUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(emailVerificationInput),
            schema: User,
        });
    };
};

export const getVerifyEmailMutationKey = () => ['verifyEmail'] as const;

export const useVerifyEmailMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useVerifyEmailHook>>>,
        TError,
        VerifyEmailMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useVerifyEmailHook>>>,
    TError,
    VerifyEmailMutationVariables,
    TContext
> => {
    const mutationKey = getVerifyEmailMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const verifyEmail = useVerifyEmailHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useVerifyEmailHook>>>,
        VerifyEmailMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return verifyEmail(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/me/email/verify` },
        { operationId: 'verify_email', operationName: 'verifyEmail' }
    );

    return customOptions;
};

export type VerifyEmailMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useVerifyEmailHook>>>>;
export type VerifyEmailMutationBody = EmailVerificationInput;
export type VerifyEmailMutationError = ErrorType<ErrorResponse>;
export type VerifyEmailMutationVariables = { data: EmailVerificationInput };

/**
 * @summary Verify Email
 */
export const useVerifyEmail = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useVerifyEmailHook>>>,
            TError,
            VerifyEmailMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useVerifyEmailHook>>>,
    TError,
    VerifyEmailMutationVariables,
    TContext
> => {
    return useMutation(useVerifyEmailMutationOptions(options), queryClient);
};
export const getSignOutUrl = () => {
    return `/auth/sign-out`;
};

/**
 * @summary Sign Out
 */
export const useSignOutHook = (): ((options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getSignOutUrl(), {
            ...options,
            method: 'POST',
        });
    };
};

export const getSignOutMutationKey = () => ['signOut'] as const;

export const useSignOutMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSignOutHook>>>, TError, void, TContext>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSignOutHook>>>, TError, void, TContext> => {
    const mutationKey = getSignOutMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const signOut = useSignOutHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSignOutHook>>>, void> = () => {
        return signOut(requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/auth/sign-out` },
        { operationId: 'sign_out', operationName: 'signOut' }
    );

    return customOptions;
};

export type SignOutMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSignOutHook>>>>;

export type SignOutMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Sign Out
 */
export const useSignOut = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSignOutHook>>>, TError, void, TContext>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useSignOutHook>>>, TError, void, TContext> => {
    return useMutation(useSignOutMutationOptions(options), queryClient);
};
export const getSendSignInCodeUrl = () => {
    return `/auth/codes`;
};

/**
 * @summary Send Sign In Code
 */
export const useSendSignInCodeHook = (): ((
    signInCodeInput: SignInCodeInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SignInCodeSent>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (signInCodeInput: SignInCodeInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getSendSignInCodeUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(signInCodeInput),
            schema: SignInCodeSent,
        });
    };
};

export const getSendSignInCodeMutationKey = () => ['sendSignInCode'] as const;

export const useSendSignInCodeMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSendSignInCodeHook>>>,
        TError,
        SendSignInCodeMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSendSignInCodeHook>>>,
    TError,
    SendSignInCodeMutationVariables,
    TContext
> => {
    const mutationKey = getSendSignInCodeMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const sendSignInCode = useSendSignInCodeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSendSignInCodeHook>>>,
        SendSignInCodeMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return sendSignInCode(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/auth/codes` },
        { operationId: 'send_sign_in_code', operationName: 'sendSignInCode' }
    );

    return customOptions;
};

export type SendSignInCodeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSendSignInCodeHook>>>>;
export type SendSignInCodeMutationBody = SignInCodeInput;
export type SendSignInCodeMutationError = ErrorType<ErrorResponse>;
export type SendSignInCodeMutationVariables = { data: SignInCodeInput };

/**
 * @summary Send Sign In Code
 */
export const useSendSignInCode = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSendSignInCodeHook>>>,
            TError,
            SendSignInCodeMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSendSignInCodeHook>>>,
    TError,
    SendSignInCodeMutationVariables,
    TContext
> => {
    return useMutation(useSendSignInCodeMutationOptions(options), queryClient);
};
export const getVerifySignInCodeUrl = () => {
    return `/auth/codes/verify`;
};

/**
 * @summary Verify Sign In Code
 */
export const useVerifySignInCodeHook = (): ((
    signInCodeVerification: SignInCodeVerification,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SignInResult>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        signInCodeVerification: SignInCodeVerification,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getVerifySignInCodeUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(signInCodeVerification),
            schema: SignInResult,
        });
    };
};

export const getVerifySignInCodeMutationKey = () => ['verifySignInCode'] as const;

export const useVerifySignInCodeMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useVerifySignInCodeHook>>>,
        TError,
        VerifySignInCodeMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useVerifySignInCodeHook>>>,
    TError,
    VerifySignInCodeMutationVariables,
    TContext
> => {
    const mutationKey = getVerifySignInCodeMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const verifySignInCode = useVerifySignInCodeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useVerifySignInCodeHook>>>,
        VerifySignInCodeMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return verifySignInCode(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/auth/codes/verify` },
        { operationId: 'verify_sign_in_code', operationName: 'verifySignInCode' }
    );

    return customOptions;
};

export type VerifySignInCodeMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useVerifySignInCodeHook>>>
>;
export type VerifySignInCodeMutationBody = SignInCodeVerification;
export type VerifySignInCodeMutationError = ErrorType<ErrorResponse>;
export type VerifySignInCodeMutationVariables = { data: SignInCodeVerification };

/**
 * @summary Verify Sign In Code
 */
export const useVerifySignInCode = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useVerifySignInCodeHook>>>,
            TError,
            VerifySignInCodeMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useVerifySignInCodeHook>>>,
    TError,
    VerifySignInCodeMutationVariables,
    TContext
> => {
    return useMutation(useVerifySignInCodeMutationOptions(options), queryClient);
};
export const getListSsoProvidersUrl = () => {
    return `/auth/sso/providers`;
};

/**
 * @summary List Sso Providers
 */
export const useListSsoProvidersHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SsoProvider[]>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListSsoProvidersUrl(), {
            ...options,
            method: 'GET',

            schema: zod.array(SsoProvider),
        });
    };
};

export const getListSsoProvidersQueryKey = () => {
    return ['auth', 'sso', 'providers'] as const;
};

export const useListSsoProvidersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
    TError = ErrorType<ErrorResponse>,
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>, TError, TData>>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListSsoProvidersQueryKey();

    const listSsoProviders = useListSsoProvidersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>> = ({ signal }) =>
        listSsoProviders({ signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        {},
        { url: `/auth/sso/providers`, operationId: 'list_sso_providers', operationName: 'listSsoProviders' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSsoProvidersQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>>;
export type ListSsoProvidersQueryError = ErrorType<ErrorResponse>;

export function useListSsoProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSsoProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSsoProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Sso Providers
 */

export function useListSsoProviders<
    TData = Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListSsoProvidersHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListSsoProvidersQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getStartSsoUrl = () => {
    return `/auth/sso/google`;
};

/**
 * @summary Start Sso
 */
export const useStartSsoHook = (): ((
    ssoStart: SsoStart,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<SsoStartResult>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (ssoStart: SsoStart, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getStartSsoUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(ssoStart),
            schema: SsoStartResult,
        });
    };
};

export const getStartSsoMutationKey = () => ['startSso'] as const;

export const useStartSsoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useStartSsoHook>>>,
        TError,
        StartSsoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useStartSsoHook>>>,
    TError,
    StartSsoMutationVariables,
    TContext
> => {
    const mutationKey = getStartSsoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const startSso = useStartSsoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useStartSsoHook>>>,
        StartSsoMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return startSso(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/auth/sso/google` },
        { operationId: 'start_sso', operationName: 'startSso' }
    );

    return customOptions;
};

export type StartSsoMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useStartSsoHook>>>>;
export type StartSsoMutationBody = SsoStart;
export type StartSsoMutationError = ErrorType<ErrorResponse>;
export type StartSsoMutationVariables = { data: SsoStart };

/**
 * @summary Start Sso
 */
export const useStartSso = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useStartSsoHook>>>,
            TError,
            StartSsoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useStartSsoHook>>>,
    TError,
    StartSsoMutationVariables,
    TContext
> => {
    return useMutation(useStartSsoMutationOptions(options), queryClient);
};
